<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AppSidebar, { type NavItem } from '@/components/layout/AppSidebar.vue'
import AppTopbar from '@/components/layout/AppTopbar.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import { useAuthStore } from '@/stores/auth'

const { t } = useI18n()
const auth = useAuthStore()

/**
 * Instructors share this shell but see fewer entries. The sidebar is filtered
 * by the same capability checks the route guards use, so a hidden link and a
 * blocked route can never disagree.
 */
const items = computed<NavItem[]>(() => {
  const base: NavItem[] = [{ to: '/admin/courses', labelKey: 'nav.adminCourses', icon: 'book' }]

  if (auth.hasCapability('journey.manage')) {
    base.push({ to: '/admin/journeys', labelKey: 'nav.adminJourneys', icon: 'route' })
  }
  if (auth.hasCapability('user.manage')) {
    base.push({ to: '/admin/users', labelKey: 'nav.adminUsers', icon: 'users' })
  }
  if (auth.hasCapability('report.view')) {
    base.push({ to: '/admin/reports', labelKey: 'nav.adminReports', icon: 'chart' })
  }
  if (auth.hasCapability('settings.manage')) {
    base.push({ to: '/admin/settings', labelKey: 'nav.adminSettings', icon: 'settings' })
  }

  return base
})
</script>

<template>
  <div class="flex min-h-full flex-col bg-stone-50 dark:bg-neutral-900">
    <AppTopbar />

    <div class="flex min-h-0 flex-1">
    <AppSidebar :items="items" heading-key="nav.adminArea">
      <template #footer>
        <BaseButton to="/dashboard" size="sm" icon="arrow-left" icon-aside block>
          {{ t('nav.backToLearning') }}
        </BaseButton>
      </template>
    </AppSidebar>

    <div class="flex min-w-0 flex-1 flex-col">
      <a
        href="#main-content"
        class="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-3 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:shadow"
      >
        {{ t('nav.skipToContent') }}
      </a>

      <main id="main-content" class="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <slot />
      </main>
    </div>
    </div>
  </div>
</template>
